/**
 * APX WorkCenter — NotificationBell
 * In-app push notification center for all roles.
 * Polls every 30s for new notifications addressed to the current user.
 */

import { useState, useRef, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useLocation } from "wouter";

const TYPE_COLORS: Record<string, string> = {
  task: "oklch(0.35 0.01 240)",
  alert: "oklch(0.55 0.01 240)",
  info: "oklch(0.35 0.01 240)",
  pl_request: "oklch(0.55 0.01 240)",
  file: "oklch(0.35 0.01 240)",
};

const TYPE_LABELS: Record<string, string> = {
  task: "Task",
  alert: "Alert",
  info: "Info",
  pl_request: "P&L",
  file: "File",
};

function formatTime(date: Date | string) {
  const d = new Date(date);
  const now = new Date();
  const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function NotificationBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const [, navigate] = useLocation();

  // Stabilize query inputs — inline objects create new references every render → infinite loop
  const userEmail = user?.email ?? "";
  const userRole = user?.role ?? "";
  const { data: notifications = [], refetch } = trpc.notifications.list.useQuery(
    { email: userEmail, role: userRole },
    { enabled: !!user, refetchInterval: 30_000 }
  );

  const markRead = trpc.notifications.markRead.useMutation({ onSuccess: () => refetch() });
  const markAllRead = trpc.notifications.markAllRead.useMutation({ onSuccess: () => refetch() });
  const clearAll = trpc.notifications.clearAll.useMutation({ onSuccess: () => refetch() });

  const unread = notifications.filter((n) => !n.isRead).length;

  // Close panel on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Toast for new notifications (track count)
  const prevCount = useRef(0);
  useEffect(() => {
    if (unread > prevCount.current && prevCount.current > 0) {
      toast.info("New notification", { description: "You have a new message in your inbox." });
    }
    prevCount.current = unread;
  }, [unread]);

  if (!user) return null;

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative flex items-center justify-center w-10 h-10 rounded-lg transition-all duration-150"
        style={{
          background: open ? "oklch(0.82 0.02 85)" : "oklch(0.88 0.02 85)",
          border: "1px solid oklch(0.75 0.02 85)",
          color: "oklch(0.12 0.04 240)",
        }}
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unread > 0 && (
          <span
            className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold z-10"
            style={{ background: "oklch(0.55 0.01 240)", color: "white" }}
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Dropdown panel — light theme */}
      {open && (
        <div
          className="absolute right-0 top-10 w-80 rounded-xl shadow-xl z-50 overflow-hidden"
          style={{
            background: "oklch(0.99 0.002 85)",
            border: "1px solid oklch(0.88 0.01 240)",
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: "oklch(0.90 0.01 240)" }}
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold" style={{ color: "oklch(0.12 0.04 240)", fontFamily: "'Playfair Display', serif" }}>
                Notifications
              </span>
              {unread > 0 && (
                <span
                  className="text-sm px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ background: "oklch(0.92 0.005 240 / 12%)", color: "oklch(0.08 0.01 240)" }}
                >
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button
                  onClick={() => markAllRead.mutate({ email: user.email, role: user.role })}
                  className="text-sm transition-colors"
                  style={{ color: "oklch(0.45 0.03 240)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.28 0.04 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.45 0.03 240)"; }}
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => {
                    if (confirm("Clear all notifications? This cannot be undone.")) {
                      clearAll.mutate({ email: user.email, role: user.role });
                    }
                  }}
                  className="text-sm transition-colors"
                  style={{ color: "oklch(0.55 0.18 25)" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.40 0.18 25)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "oklch(0.55 0.18 25)"; }}
                  title="Delete all notifications"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-3"
                  style={{ background: "oklch(0.93 0.01 240)" }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: "oklch(0.08 0.01 240)" }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <p className="text-sm" style={{ color: "oklch(0.28 0.04 240)" }}>No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n.id}
                  className="flex items-start gap-3 px-4 py-3 transition-colors"
                  style={{
                    background: n.isRead ? "transparent" : "oklch(0.96 0.01 75)",
                    borderBottom: "1px solid oklch(0.92 0.01 240)",
                    cursor: (n as any).sourceUrl ? "pointer" : "default",
                  }}
                  onClick={() => {
                    if (!n.isRead) markRead.mutate({ id: n.id });
                    const sourceUrl = (n as any).sourceUrl;
                    // Determine navigation target:
                    // - If sourceUrl exists, use it (deep-link to specific thread/task)
                    // - If no sourceUrl but it's an SMS notification, fall back to general SMS inbox
                    const isSmsNotif = n.title?.toLowerCase().includes("sms") || n.title?.toLowerCase().includes("mms");
                    const navTarget = sourceUrl ?? (isSmsNotif ? "/sms-inbox" : null);
                    if (navTarget) {
                      setOpen(false);
                      window.location.href = navTarget;
                    }
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "oklch(0.94 0.008 240)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = n.isRead ? "transparent" : "oklch(0.96 0.01 75)"; }}
                  title={(n as any).sourceUrl ? "Click to view source" : undefined}
                >
                  {/* Type dot */}
                  <div className="shrink-0 mt-0.5">
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[8px] font-bold"
                      style={{
                        background: `${TYPE_COLORS[n.type] ?? TYPE_COLORS.info}15`,
                        color: TYPE_COLORS[n.type] ?? TYPE_COLORS.info,
                      }}
                    >
                      {(TYPE_LABELS[n.type] ?? "?").charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: "oklch(0.12 0.04 240)" }}>{n.title}</p>
                      {!n.isRead && (
                        <span className="shrink-0 w-1.5 h-1.5 rounded-full" style={{ background: "oklch(0.55 0.01 240)" }} />
                      )}
                    </div>
                    <p className="text-sm leading-snug mt-0.5" style={{ color: "oklch(0.22 0.04 240)" }}>{n.message}</p>
                    {/* File attachment download link */}
                    {n.type === "file" && (n as any).fileTransfer?.fileUrl && (
                      <a
                        href={(n as any).fileTransfer.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download={(n as any).fileTransfer.fileName ?? true}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 mt-1 text-sm font-semibold px-2 py-0.5 rounded-md transition-colors"
                        style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.28 0.04 240)", border: "1px solid oklch(0.85 0.01 240)" }}
                      >
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        {(n as any).fileTransfer.fileName ?? "Download File"}
                      </a>
                    )}
                    <p className="text-sm mt-1" style={{ color: "oklch(0.28 0.04 240)" }}>
                      {n.senderName} · {formatTime(n.createdAt)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
